/**
 * 共通シェル（03 5.2・7 章）：AppBar＋サイドメニュー（PC は常時 180px、768px 以下は ☰ で開く 280px の一時ドロワー）＋本文。
 * 「本文へ移動」を AppBar の前に置く（03 3.16 の 8）。
 * 見た目は旧PCRM（components/common/Layout.css）に合わせる：白いヘッダー＋下線・オレンジの太字ロゴ、
 * メニューは小さめの文字、選択中は薄いオレンジの背景＋左の縦線。
 * 262 r1：PC の寸法も旧に合わせる（ヘッダー 52px＋下線 1px・サイドメニュー 180px・項目は上下 8px＋13px×行高 1.6・
 * 見出しは 10px で下に 8px・区切りごとに 24px 空ける・本文の余白は上 24px／左 48px（旧のつまみ 8px＋余白 40px）／右 40px）。
 * 768px 以下（モバイル）は今までどおり。
 */
import { MenuIcon, PersonIcon as PersonOutlineIcon } from '../../components/icons/LegacyIcons';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useRuntimeConfig } from '../config/app-config-context';
import { EnvBadge } from '../../components/status/EnvBadge';
import { useAuthSession } from '../../features/auth/components/AuthSessionProvider';
import { usePermission } from '../../features/auth/hooks/usePermission';
import { useMe } from '../../features/auth/queries';
import { DRAWER_WIDTH, LEGACY_COLORS, MOBILE_DRAWER_WIDTH } from '../theme';
import { SIDE_MENU, USER_MENU, isMenuItemActive } from './menu-definitions';

const MAIN_CONTENT_ID = 'pcrm-main-content';

/** 旧のヘッダー：上下 8px＋アバター 36px＝52px（下線 1px は別）。モバイルは今までどおり 56px */
const HEADER_TOOLBAR_SX = { minHeight: { md: 52 } } as const;
/** ヘッダーの下に本文・メニューを置くための空き（下線 1px も含める） */
const HEADER_SPACER_SX = { minHeight: { md: 53 } } as const;

/** PC（旧 Layout.css の .sidebar-title）：10px・行高 1.6・下に 8px */
const LEGACY_SUBHEADER_SX = {
  position: 'static',
  lineHeight: 1.6,
  bgcolor: 'transparent',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.5px',
  color: LEGACY_COLORS.text_tertiary,
  px: 1.5,
  mb: 1,
} as const;

const SUBHEADER_SX = {
  lineHeight: '32px',
  bgcolor: 'transparent',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.5px',
  color: LEGACY_COLORS.text_tertiary,
  px: 1.5,
} as const;

const MENU_ITEM_SX = {
  minHeight: 36,
  px: 1.5,
  py: 1,
  color: 'text.secondary',
  position: 'relative',
  transition: 'all 0.2s',
  '& .MuiListItemText-primary': { fontSize: 13 },
  '&:hover': { bgcolor: LEGACY_COLORS.bg_hover, color: 'text.primary' },
  '&.Mui-selected, &.Mui-selected:hover': { bgcolor: LEGACY_COLORS.primary_selected, color: 'primary.main' },
  '&.Mui-selected .MuiListItemText-primary': { fontWeight: 500 },
  '&.Mui-selected::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    background: 'linear-gradient(180deg, #F97316 0%, #EA580C 50%, #9A3412 100%)',
  },
} as const;

/** PC（旧 .sidebar-item）：上下 8px・左右 12px・13px×行高 1.6（1 行 36.8px） */
const LEGACY_MENU_ITEM_SX = {
  ...MENU_ITEM_SX,
  minHeight: 0,
  py: 1,
  '& .MuiListItemText-root': { my: 0 },
  '& .MuiListItemText-primary': { fontSize: 13, lineHeight: 1.6 },
} as const;

function SideMenuList({ onNavigate, is_legacy_size = false }: { onNavigate?: () => void; is_legacy_size?: boolean }) {
  const permission = usePermission();
  const location = useLocation();
  return (
    <Box component="nav" aria-label="メインメニュー" sx={{ bgcolor: 'background.paper' }}>
      {SIDE_MENU.map((group) => {
        const items = group.items.filter((item) => !item.permission || permission.can(item.permission));
        if (items.length === 0) {
          return null;
        }
        return (
          <List
            key={group.heading ?? 'main'}
            dense
            disablePadding={is_legacy_size}
            sx={is_legacy_size ? { mb: 3 } : undefined}
            subheader={
              group.heading ? (
                <ListSubheader
                  component="div"
                  disableSticky={is_legacy_size}
                  sx={is_legacy_size ? LEGACY_SUBHEADER_SX : SUBHEADER_SX}
                >
                  {/* 旧PCRMどおり見出しは「管理」だけ（262）。管理者以外には見出しごと出さない */}
                  {group.heading}
                </ListSubheader>
              ) : undefined
            }
          >
            {items.map((item) => (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                selected={isMenuItemActive(item.to, location.pathname)}
                onClick={onNavigate}
                sx={is_legacy_size ? LEGACY_MENU_ITEM_SX : MENU_ITEM_SX}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        );
      })}
    </Box>
  );
}

function UserMenuButton() {
  const me = useMe({ is_fetch_enabled: false });
  const permission = usePermission();
  const auth_session = useAuthSession();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const user_name = me.data?.user_name ?? '';
  const items = USER_MENU.filter((item) => !item.permission || permission.can(item.permission));
  return (
    <>
      <Button
        color="inherit"
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={anchor ? 'true' : 'false'}
        aria-label="ユーザーメニュー"
        sx={{ gap: 1.5, color: 'text.primary', p: 0, minHeight: 0, '&:hover': { bgcolor: 'transparent' } }}
      >
        {/* 旧どおり：右寄せの 2 行（利用者名 13px 太字／ロール名 11px 灰色）＋右に人のアイコンの丸 */}
        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <Box
            component="span"
            sx={{
              display: { xs: 'none', md: 'block' },
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.2,
              color: 'text.primary',
            }}
          >
            {user_name}
          </Box>
          {me.data && (
            <Box component="span" sx={{ fontSize: 11, fontWeight: 400, lineHeight: 1.2, color: 'text.secondary' }}>
              {me.data.role_name}
            </Box>
          )}
        </Box>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: LEGACY_COLORS.bg_tertiary,
            color: 'text.secondary',
            border: `2px solid ${LEGACY_COLORS.border}`,
            '.MuiButton-root:hover &': { borderColor: 'primary.main', color: 'primary.main' },
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 20 }} />
        </Avatar>
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {items.map((item) => (
          <MenuItem
            key={item.to}
            onClick={() => {
              setAnchor(null);
              navigate(item.to);
            }}
          >
            {item.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void auth_session.logout();
          }}
        >
          ログアウト
        </MenuItem>
      </Menu>
    </>
  );
}

/** シェルの枠（本文を children で受ける。404 画面もこれを使う） */
export function AppShellFrame({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const is_mobile = useMediaQuery(theme.breakpoints.down('md'));
  const config = useRuntimeConfig();
  const [is_drawer_open, setIsDrawerOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Link
        href={`#${MAIN_CONTENT_ID}`}
        sx={{
          position: 'absolute',
          left: -9999,
          '&:focus': { left: 8, top: 8, zIndex: theme.zIndex.tooltip, bgcolor: 'background.paper', p: 1 },
        }}
      >
        本文へ移動
      </Link>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: `1px solid ${LEGACY_COLORS.border}`,
        }}
      >
        <Toolbar sx={{ px: { md: 3 }, ...HEADER_TOOLBAR_SX }}>
          {is_mobile && (
            <IconButton
              color="inherit"
              edge="start"
              aria-label="メニューを開く"
              onClick={() => setIsDrawerOpen(true)}
              sx={{ mr: 1, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            component={RouterLink}
            to="/"
            variant="h6"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: 18,
              fontWeight: 800,
              textShadow: '0 1px 3px rgba(194, 65, 12, 0.3)',
            }}
          >
            Pcrm Cloud
          </Typography>
          <EnvBadge app_env={config.app_env} />
          <Box sx={{ flexGrow: 1 }} />
          <UserMenuButton />
        </Toolbar>
      </AppBar>
      {is_mobile ? (
        <Drawer
          variant="temporary"
          open={is_drawer_open}
          onClose={() => setIsDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: MOBILE_DRAWER_WIDTH, py: 1.5 } }}
          data-testid="pcrm-drawer-temporary"
        >
          <Toolbar />
          <SideMenuList onNavigate={() => setIsDrawerOpen(false)} />
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            // 紙（背景・右の線）を画面の高さに固定せず、本文の高さいっぱいまで伸ばす（本文が長い画面で途中で切れないように）。
            // メニューは中の sticky の箱で画面の上に留める
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
              overflowY: 'visible',
              borderRight: `1px solid ${LEGACY_COLORS.border}`,
            },
          }}
          data-testid="pcrm-drawer-permanent"
        >
          <Box sx={{ position: 'sticky', top: 0, maxHeight: '100vh', overflowY: 'auto' }}>
            <Toolbar sx={HEADER_SPACER_SX} />
            {/* 旧 .sidebar の上の余白 12px */}
            <Box sx={{ pt: 1.5 }}>
              <SideMenuList is_legacy_size />
            </Box>
          </Box>
        </Drawer>
      )}
      <Box
        component="main"
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pl: { xs: 1.5, md: '48px' },
          pr: { xs: 1.5, md: '40px' },
          pt: { xs: 1.5, md: 3 },
          pb: { xs: 1.5, md: 3 },
        }}
      >
        <Toolbar sx={HEADER_SPACER_SX} />
        {children}
      </Box>
    </Box>
  );
}

export function AppShell() {
  return (
    <AppShellFrame>
      <Outlet />
    </AppShellFrame>
  );
}
